import React, { useEffect, useState } from 'react';
import { useSelector, shallowEqual, useDispatch } from 'react-redux';
import { useFela } from 'react-fela';
import PropTypes from 'prop-types';
import * as playerActions from '../../../actions/player-events';
import * as controlsActions from '../../../actions/controls';
import {
  selectIsSeekEnabled,
  selectNormalizedInterval,
  selectNormalizedCurrentTime,
} from '../../../selectors';
import { usePlayer } from '../../../PlayerContext';
import platform, { OS_TYPES } from '../../../../player/utils/platform';

const rules = ({ seekEnabled, thumbWidth, thumbHeight }) => ({
  cursor: seekEnabled ? 'pointer' : 'default',
  left: 0,
  margin: 0,
  opacity: 0,
  position: 'absolute',
  top: '-7px',
  width: '100%',
  height: '14px',
  '-webkit-appearance': 'none',
  userSelect: 'none',

  '&::-webkit-slider-thumb': {
    '-webkit-appearance': 'none',
    appearance: 'none',
    width: `${thumbWidth}px`, /* Thumb width */
    height: `${thumbHeight}px`, /* Thumb height */
    borderRadius: '50%',
    cursor: seekEnabled ? 'pointer' : 'default',
  },
});

const Slider = React.forwardRef(({ onMouseMove, onMouseOut, thumbWidth, thumbHeight }, ref) => {
  const dispatch = useDispatch();
  const player = usePlayer();

  const seekEnabled = useSelector(selectIsSeekEnabled);
  const [start, , duration] = useSelector(selectNormalizedInterval, shallowEqual);
  const currentTime = useSelector(selectNormalizedCurrentTime);
  const isLive = useSelector(state => state.playback.isLive);
  const isMobile = [OS_TYPES.IOS, OS_TYPES.ANDROID].includes(platform.os);
  const [awaitingChange, setAwaitingChange] = useState({ status: false });
  const [hasChanged, setHasChanged] = useState(false);

  const { css } = useFela({ seekEnabled, thumbWidth, thumbHeight });

  const onChange = (e) => {
    if (seekEnabled) {
      if (awaitingChange.status && e.target.value !== awaitingChange.startingValue) {
        setHasChanged(true);
      }
      dispatch(controlsActions.setSeekingTime(parseInt(e.target.value, 10)));
    }
  };

  const onPressStart = (e) => {
    if (seekEnabled) {
      if (e.type === 'mousedown' && e.button === 0) { // Left mouse button
        dispatch(controlsActions.setSeekingTime(parseInt(e.target.value, 10)));
        dispatch(controlsActions.setSeekMode(true));
      } else if (e.type === 'touchstart' && e.touches.length === 1) { // Single touch
        setAwaitingChange({ status: true, startingValue: e.target.value });
        dispatch(controlsActions.setSeekingTime(parseInt(e.target.value, 10)));
        dispatch(controlsActions.setSeekMode(true));
      }
    }
  };

  const onPressEnd = (e) => {
    if (seekEnabled && (
      (e.type === 'mouseup' && e.button === 0) // Left mouse button
      || (e.type === 'touchend' && e.touches.length === 0) // all finger lifted
    )) {
      const time = parseInt(e.target.value, 10);
      const normalizedTime = isLive ? start + time : time;
      player.seek(normalizedTime);
      dispatch(controlsActions.setSeekMode(false));
      dispatch(playerActions.setCurrentTime(normalizedTime));
      setAwaitingChange({ status: false });
      setHasChanged(false);
    }
  };

  const onTouchEnd = (e) => {
    // to fix issue on mobile where touchEnd is triggered before onChange
    if (hasChanged) {
      onPressEnd(e);
    } else {
      setAwaitingChange({ ...awaitingChange, event: e });
    }
  };

  useEffect(() => {
    // only perform pressEnd if we are awaiting value to change and value has changed
    if (hasChanged && awaitingChange.status && awaitingChange.event) {
      onPressEnd(awaitingChange.event);
    }
  }, [hasChanged, awaitingChange]);

  return (
    <input
      ref={ref}
      className={css(rules)}
      type="range"
      min="0"
      max={duration}
      {...isMobile ? {
        onTouchStart: onPressStart,
        onTouchEnd,
      } : {
        onMouseDown: onPressStart,
        onMouseUp: onPressEnd,
        onMouseMove,
        onMouseOut,
      }}
      onChange={onChange}
      step="1"
      value={currentTime}
    />
  );
});

Slider.displayName = 'Slider';

Slider.propTypes = {
  onMouseMove: PropTypes.func,
  onMouseOut: PropTypes.func,
  thumbHeight: PropTypes.number,
  thumbWidth: PropTypes.number,
};

export default React.memo(Slider);
